// A digital employee's own KPIs (AGENT-KPI-TOOL; review build item #9; 13 §Ingest contract, 05
// §Agent scopes, 04 §Auth model). Two runtime tools, both on the caller's OWN seat only:
//
//   kpis.read        `GET agent/kpis`: the seat's metrics with target, cadence, the current
//                    period and when it is due, the newest value and the recent ones (scope
//                    `kpis:read`). The runtime puts the same rows in the wake prompt as data.
//   outcomes.record  `POST agent/outcomes`: one value (a manual metric: an observation with
//                    `source = agent`) or one event (an event metric: an ingest item the KPI
//                    projection counts), with the run's provenance and the agent's evidence
//                    (scope `kpis:write`). Idempotent per (metric, period, run).
//
// The credential is the run's capability token (a DE's run is bound to its seat: no hand-made
// key), or the DE's agent key with the scope and, for a record, `X-CompanyOS-Run` naming its own
// running run. A metric not on the seat, a value for an event metric (or the reverse) and a unit
// other than the metric's are refused. AGENT-REPORTING (13 §Reporting duties, founder-approved
// 2026-10-21): each row carries the seat's reporting duty (when it is due, the generated order
// text) and whether the metric needs review; a record on a `needs_review` metric is held as a
// `kpi_value` approval (`status: pending_review`) and counts nowhere until a person decides it; a
// duty with `evidence_required` needs `evidence.url` (422 `evidence_required`).
import { z } from "zod";
import {
  MetricCadence,
  MetricDirection,
  MetricSource,
  MetricUnit,
  PeriodType,
  TrackingMode,
} from "../enums";
import { PeriodKey, Timestamp, Uuid } from "../primitives";
import { ReportingStatus } from "../reporting";
import { TaskMetricQuery } from "../rows";
import { LastReport } from "../scorecard";
import { CompanyParams } from "./common";
import { defineRoute } from "./route";

const base = "/v1/companies/{co}";
const kpi = { feature: "kpi" } as const;

/** The scopes of the KPI tools (`{resource}:{access}`, 04). */
export const KPI_READ_SCOPE = "kpis:read";
export const KPI_WRITE_SCOPE = "kpis:write";

/** How the agent reports a metric: a value, an event the projection counts, or not at all. */
export const AgentReportKind = z.enum(["value", "event", "none"]);
export type AgentReportKind = z.infer<typeof AgentReportKind>;

export const AgentKpiPeriod = z.object({
  type: PeriodType,
  key: PeriodKey,
  starts_at: Timestamp,
  ends_at: Timestamp,
});

export const AgentKpi = z
  .object({
    metric_def_id: Uuid,
    key: z.string(),
    name: z.string(),
    unit: MetricUnit,
    direction: MetricDirection,
    source: MetricSource,
    cadence: MetricCadence.nullable(),
    tracking_mode: TrackingMode,
    /** `value`: send `kind: value`; `event`: `kind: event`; `none`: derived (formula), not reported. */
    report: AgentReportKind,
    /** An event metric's item type (what `kind: event` records), else null. */
    event_type: z.string().nullable(),
    /** The cadence's current period (null without a cadence). */
    period: AgentKpiPeriod.nullable(),
    /** When the current period's number is due: the period's end. */
    due_at: Timestamp.nullable(),
    target: z.number().nullable(),
    target_max: z.number().nullable(),
    /** The current period's value so far (null: nothing yet). */
    actual: z.number().nullable(),
    last: LastReport.nullable(),
    /** The previous periods of the cadence, oldest first (at most 8). */
    recent: z.array(z.object({ period_key: PeriodKey, value: z.number().nullable() })).max(8),
    /** AGENT-REPORTING: a value recorded here waits for a person's review before it counts. */
    needs_review: z.boolean(),
    /** AGENT-REPORTING: the seat's reporting duty for the metric (null: none). */
    reporting: ReportingStatus.nullable(),
    /** AGENT-REPORTING: the duty as the agent's standing order (null: no duty). */
    order_text: z.string().nullable(),
    /** T3-TASK-ROLLUPS: a `task` metric's board and what it counts there (else null). */
    task_query: TaskMetricQuery.nullable(),
  })
  .meta({ id: "AgentKpi" });
export type AgentKpi = z.infer<typeof AgentKpi>;

export const AgentKpis = z
  .object({
    seat: z.object({ id: Uuid, title: z.string() }),
    as_of: Timestamp,
    /** The caller's KPI scopes (a run token's are its seat's; a key's are its own). */
    scopes: z.array(z.string()),
    metrics: z.array(AgentKpi),
  })
  .meta({ id: "AgentKpis" });
export type AgentKpis = z.infer<typeof AgentKpis>;

export const OutcomeEvidence = z.strictObject({
  /** What the agent did that the number stands for, in a sentence or two. */
  text: z.string().trim().min(1).max(500),
  /** A link to the work (a task, a message, a document), when there is one. */
  url: z
    .url({ protocol: /^https?$/ })
    .max(500)
    .optional(),
});

export const OutcomeRecord = z
  .strictObject({
    metric_key: z
      .string()
      .max(64)
      .regex(/^[a-z][a-z0-9_]*(\.[a-z][a-z0-9_]*)*$/, "the metric's dotted key"),
    kind: z.enum(["value", "event"]),
    /** `kind: value`: the period's value. */
    value: z.number().finite().optional(),
    /** `kind: event`: how many of the event (default 1). */
    quantity: z.number().finite().positive().max(1_000_000).optional(),
    /** Optional check: the unit the agent believes the metric has (refused when it differs). */
    unit: MetricUnit.optional(),
    /** `kind: value`: the period (default: the cadence's current period). */
    period_key: PeriodKey.optional(),
    /** `kind: event`: when it happened (default: now). */
    occurred_at: Timestamp.optional(),
    evidence: OutcomeEvidence,
  })
  .superRefine((b, ctx) => {
    if (b.kind === "value") {
      if (b.value === undefined)
        ctx.addIssue({ code: "custom", path: ["value"], message: "required for kind value" });
      for (const k of ["quantity", "occurred_at"] as const)
        if (b[k] !== undefined)
          ctx.addIssue({ code: "custom", path: [k], message: "only for kind event" });
    } else
      for (const k of ["value", "period_key"] as const)
        if (b[k] !== undefined)
          ctx.addIssue({ code: "custom", path: [k], message: "only for kind value" });
  })
  .meta({ id: "OutcomeRecord" });
export type OutcomeRecord = z.infer<typeof OutcomeRecord>;

export const OutcomeRecorded = z
  .object({
    /**
     * `duplicate`: this run already recorded the same thing for the metric's period;
     * `pending_review` (AGENT-REPORTING): the metric needs review, the value waits as an approval.
     */
    status: z.enum(["recorded", "duplicate", "pending_review"]),
    kind: z.enum(["value", "event"]),
    metric_def_id: Uuid,
    metric_key: z.string(),
    period_type: PeriodType,
    period_key: PeriodKey,
    run_id: Uuid,
    observation_id: Uuid.nullable(),
    event_id: Uuid.nullable(),
    /** AGENT-REPORTING: the `kpi_value` approval holding a value pending review. */
    approval_id: Uuid.nullable(),
    /** The run step that shows the record in the run's timeline. */
    step_id: Uuid,
  })
  .meta({ id: "OutcomeRecorded" });
export type OutcomeRecorded = z.infer<typeof OutcomeRecorded>;

/**
 * T3-TASK-ROLLUPS: a DE proposes a task metric (a KPI counted from a board's tasks) for a
 * company admin to confirm. Confirmed, the metric is created (source `task`) and put on the
 * board owner's seat (the board's lead seat; the proposing DE's seat when the board has none).
 */
export const TaskMetricProposal = z
  .strictObject({
    name: z.string().trim().min(1).max(120),
    /** The metric's dotted key (default: `tasks.` and a slug of the name). */
    key: z
      .string()
      .max(64)
      .regex(/^[a-z][a-z0-9_]*(\.[a-z][a-z0-9_]*)*$/, "dotted lower-case key")
      .optional(),
    cadence: MetricCadence.default("weekly"),
    unit: MetricUnit.default("number"),
    direction: MetricDirection.default("gte"),
    task_query: TaskMetricQuery,
    /** Why the metric is worth tracking, shown to the person who confirms it. */
    reason: z.string().trim().min(1).max(500),
  })
  .meta({ id: "TaskMetricProposal" });
export type TaskMetricProposal = z.infer<typeof TaskMetricProposal>;

/** The `task_metric` approval's `proposal.change`: the metric to create and the seat it goes on. */
export const TaskMetricChange = z.strictObject({
  key: z.string().max(64),
  name: z.string().max(120),
  unit: MetricUnit,
  direction: MetricDirection,
  aggregation: z.enum(["cumulative", "average"]),
  cadence: MetricCadence,
  task_query: TaskMetricQuery,
  seat_id: Uuid.nullable(),
});
export type TaskMetricChange = z.infer<typeof TaskMetricChange>;

export const TaskMetricProposed = z
  .object({ status: z.literal("pending_approval"), approval_id: Uuid, key: z.string() })
  .meta({ id: "TaskMetricProposed" });
export type TaskMetricProposed = z.infer<typeof TaskMetricProposed>;

export const agentKpiRoutes = {
  "kpis.proposeTaskMetric": defineRoute({
    ...kpi,
    id: "kpis.proposeTaskMetric",
    method: "post",
    path: `${base}/agent/task-metrics`,
    tag: "agent-kpis",
    summary: "Propose a task metric (a KPI counted from a board's tasks) for an admin (kpis:write)",
    description:
      "T3-TASK-ROLLUPS. A digital employee's own run token or agent key. Files a `task_metric` " +
      "approval decided by a company admin; approved, the metric is created and put on the " +
      "board owner's seat, and the `task_metric.snapshot` job feeds it.",
    access: "run_token",
    params: CompanyParams,
    body: TaskMetricProposal,
    idempotency: "optional",
    response: { status: 201, schema: TaskMetricProposed },
    errors: ["scope_missing", "supervisor_required", "run_not_owned"],
    emits: ["approval.requested"],
  }),
  "kpis.read": defineRoute({
    ...kpi,
    id: "kpis.read",
    method: "get",
    path: `${base}/agent/kpis`,
    tag: "agent-kpis",
    summary: "The calling agent's own seat KPIs: target, cadence, due, recent values (kpis:read)",
    access: "run_token",
    params: CompanyParams,
    response: { status: 200, schema: AgentKpis },
    errors: ["scope_missing"],
    emits: [],
  }),
  "outcomes.record": defineRoute({
    ...kpi,
    id: "outcomes.record",
    method: "post",
    path: `${base}/agent/outcomes`,
    tag: "agent-kpis",
    summary: "Record a value or an event for a metric on the agent's own seat (kpis:write)",
    access: "run_token",
    params: CompanyParams,
    body: OutcomeRecord,
    idempotency: "optional",
    response: { status: 200, schema: OutcomeRecorded },
    errors: [
      "scope_missing",
      "metric_not_on_seat",
      "report_kind_mismatch",
      "unit_mismatch",
      "source_event_conflict",
      "run_not_owned",
      "run_state_conflict",
      "evidence_required",
      "supervisor_required",
    ],
    emits: [
      "run.step_recorded",
      "observation.recorded",
      "outcome.recorded",
      "activity.recorded",
      "approval.requested",
    ],
  }),
} as const;
